import React, { useState, useMemo, useCallback } from 'react';
import {
  Box, Typography, IconButton, Button, Chip, Tooltip, TextField, Autocomplete,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Checkbox, CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import FlexService from '../../services/FlexService';

/**
 * BulkConfirmOverlay — Confirmation panel for bulk actions.
 *
 * Shows a grid of selected rows from the parent grid. User can review,
 * remove rows, and confirm the action. Status bar shows aggregate counts.
 *
 * Props:
 *   rows         - Array of selected row data objects
 *   config       - { columns, statusBar, title, confirmLabel }
 *   actionScript - FLEX script to execute on confirm
 *   colors       - Theme colors
 *   onClose      - Called when overlay is dismissed
 *   onComplete   - Called after successful execution (triggers grid refresh)
 */
/**
 * A form field whose choices come from an options Command ({value, label} rows), e.g. the
 * person to assign the selected pick lists to. Searches as the user types.
 */
const OptionsField = ({ field, value, onChange, colorsSx }) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [input, setInput] = useState('');
  React.useEffect(() => {
    let live = true;
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const resp = await FlexService.executeScript({ scriptName: field.optionsScript, context: { _payload: { search: input } } });
        const rows = resp.context?.result;
        if (live) setOptions(Array.isArray(rows) ? rows : []);
      } catch (err) {
        if (live) setOptions([]);
      } finally {
        if (live) setLoading(false);
      }
    }, 250);
    return () => { live = false; clearTimeout(timer); };
  }, [field.optionsScript, input]);
  const selected = options.find((o) => String(o.value) === String(value)) || (value ? { value, label: String(value) } : null);
  return (
    <Autocomplete
      size="small"
      sx={{ flex: 1 }}
      options={options}
      loading={loading}
      value={selected}
      filterOptions={(x) => x}
      isOptionEqualToValue={(o, v) => String(o.value) === String(v.value)}
      getOptionLabel={(o) => o?.label || ''}
      onInputChange={(_, text, reason) => { if (reason === 'input') setInput(text); }}
      onChange={(_, opt) => onChange(opt ? opt.value : '')}
      renderInput={(params) => (
        <TextField {...params} label={field.label} placeholder={field.placeholder || ''} required={field.required} sx={colorsSx} />
      )}
    />
  );
};

const BulkConfirmOverlay = ({ rows: initialRows, config, actionScript, colors, onClose, onComplete }) => {
  const [rows, setRows] = useState(initialRows || []);
  const [selectedIds, setSelectedIds] = useState(new Set(initialRows?.map((_, i) => i) || []));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // preview rows from a FLEX Command when the action isn't run on selected rows (FH-242):
  // its result is the rows, or {rows, message}
  const [loadingRows, setLoadingRows] = useState(!!config?.rowsScript);
  const [notice, setNotice] = useState(null);
  React.useEffect(() => {
    if (!config?.rowsScript) return undefined;
    let live = true;
    (async () => {
      try {
        const resp = await FlexService.executeScript({ scriptName: config.rowsScript, context: {} });
        const res = resp?.context?.result;
        const loaded = Array.isArray(res) ? res : (res?.rows || []);
        if (!live) return;
        setRows(loaded);
        setSelectedIds(new Set(loaded.map((_, i) => i)));
        setNotice(loaded.length === 0 ? (res?.message || config.emptyMessage || 'Nothing to do.') : (res?.message || null));
      } catch (err) {
        if (live) setError(err.message || 'Could not load the preview.');
      } finally {
        if (live) setLoadingRows(false);
      }
    })();
    return () => { live = false; };
  }, [config?.rowsScript]); // eslint-disable-line react-hooks/exhaustive-deps

  const columns = config?.columns || [];
  const statusBar = config?.statusBar;
  const title = config?.title || 'Confirm Action';
  const confirmLabel = config?.confirmLabel || 'Confirm';
  const formFields = config?.formFields || [];

  // Form field values — use a ref-stable key to detect config changes
  const [formValues, setFormValues] = useState({});

  // Initialize form values when formFields are available
  React.useEffect(() => {
    if (formFields.length > 0) {
      const init = {};
      formFields.forEach(f => { init[f.field] = f.defaultValue || ''; });
      setFormValues(prev => {
        // Only reset if fields changed
        const hasAll = formFields.every(f => f.field in prev);
        return hasAll ? prev : { ...prev, ...init };
      });
    }
  }, [formFields.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Remove a row
  const handleRemoveRow = useCallback((idx) => {
    setRows(prev => prev.filter((_, i) => i !== idx));
    setSelectedIds(prev => {
      const next = new Set();
      prev.forEach(id => {
        if (id < idx) next.add(id);
        else if (id > idx) next.add(id - 1);
      });
      return next;
    });
  }, []);

  // Toggle row selection
  const handleToggleRow = useCallback((idx) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  }, []);

  // Toggle all
  const handleToggleAll = useCallback(() => {
    if (selectedIds.size === rows.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(rows.map((_, i) => i)));
    }
  }, [selectedIds, rows]);

  // Compute status bar counters
  const counters = useMemo(() => {
    if (!statusBar?.counters) return [];
    return statusBar.counters.map(counter => {
      let value;
      if (counter.type === 'total') {
        value = rows.length;
      } else if (counter.type === 'sum' && counter.field) {
        value = rows.reduce((acc, r) => acc + (Number(r[counter.field]) || 0), 0);
      } else if (counter.type === 'count' && counter.field && counter.value) {
        value = rows.filter(r => r[counter.field] === counter.value).length;
      } else {
        value = rows.length;
      }
      return { ...counter, value };
    });
  }, [rows, statusBar]);

  // Confirm action
  const handleConfirm = useCallback(async () => {
    const selectedRows = rows.filter((_, i) => selectedIds.has(i));
    if (selectedRows.length === 0) {
      setError('No rows selected');
      return;
    }
    // Validate required form fields
    for (const f of formFields) {
      if (f.required && !formValues[f.field]) {
        setError(`${f.label} is required`);
        return;
      }
    }
    setSaving(true);
    setError(null);
    try {
      // Build payload: form field values + selected rows
      const payload = { ...formValues, selected_rows: selectedRows };
      const response = await FlexService.executeScript({
        scriptName: actionScript,
        context: {
          _payload: payload,
          selected_rows: selectedRows,
        },
      });
      if (response.success && response.context?.success === false) {
        // the FLEX Command ran but said it couldn't do it (success = false, error_message)
        setError(response.context?.error_message || 'Action failed');
      } else if (response.success) {
        // response.success is ENGINE-level only (did the script run without throwing).
        // Bulk planning/execute scripts like shipment_plan_selected_orders report real
        // per-row business failures (e.g. a WMS rejection) via their own result.success/
        // result.failures convention without ever throwing -- checking response.success
        // alone missed those entirely and closed the overlay as if everything succeeded,
        // silently dropping the real error (FH-56-style bug, found live 2026-10-06 via
        // "planning an order does not work" -- same root cause as useWorkspaceActions.js's
        // executeAction, which this overlay bypasses and never got that fix applied to).
        const result = response.context?.result || response;
        const failures = Array.isArray(result?.failures) ? result.failures : [];
        if (result?.success === false || failures.length > 0) {
          const detail = failures.map(f => `${f.orderNumber}: ${f.error}`).join('; ');
          setError(detail || 'Some rows failed to process.');
          // Still refresh the underlying grid -- any rows that DID succeed (partial
          // success) should disappear from the unplanned queue even though we keep
          // this overlay open so the user can see which ones failed and why.
          onComplete && onComplete(result, { keepOpen: true });
          return;
        }
        onComplete && onComplete(result);
      } else {
        const diag = response.diagnostics?.[0];
        setError(diag?.message || response.errorMessage || 'Action failed');
      }
    } catch (err) {
      setError(err.message || 'Action failed');
    } finally {
      setSaving(false);
    }
  }, [rows, selectedIds, actionScript, onComplete, formValues, formFields]);

  const allSelected = selectedIds.size === rows.length && rows.length > 0;

  return (
    <Box sx={{
      position: 'fixed', top: 0, right: 0, bottom: 0,
      // config.wide: a preview with many columns (FH-242's min/max check) needs the room
      width: config?.wide ? { xs: '100%', md: '85%', lg: '75%' } : { xs: '100%', md: '60%', lg: '50%' },
      bgcolor: colors.background, zIndex: 1300,
      display: 'flex', flexDirection: 'column',
      borderLeft: `1px solid ${colors.border}`,
      boxShadow: '-4px 0 20px rgba(0,0,0,0.3)',
    }}>
      {/* Header */}
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        px: 2, py: 1.5, borderBottom: `1px solid ${colors.border}`, bgcolor: colors.surface,
      }}>
        <Typography variant="h6" sx={{ color: colors.textPrimary, fontWeight: 600 }}>
          {title}
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: colors.textSecondary }}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Form fields (above grid) */}
      {formFields.length > 0 && (
        <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${colors.border}`, bgcolor: colors.surface }}>
          {formFields.map(f => (
            <Box key={f.field} sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
              {f.optionsScript ? (
                <OptionsField
                  field={f}
                  value={formValues[f.field]}
                  onChange={(v) => setFormValues(prev => ({ ...prev, [f.field]: v }))}
                  colorsSx={{
                    '& .MuiOutlinedInput-root': { borderRadius: 0, bgcolor: colors.background },
                    '& .MuiInputBase-input': { color: colors.textPrimary },
                    '& .MuiInputLabel-root': { color: colors.textSecondary },
                  }}
                />
              ) : (
              <TextField
                size="small"
                label={f.label}
                placeholder={f.placeholder || ''}
                value={formValues[f.field] || ''}
                onChange={(e) => setFormValues(prev => ({ ...prev, [f.field]: e.target.value }))}
                required={f.required}
                sx={{
                  flex: 1,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 0, bgcolor: colors.background,
                    '& fieldset': { borderColor: colors.border },
                    '&:hover fieldset': { borderColor: '#22c55e' },
                    '&.Mui-focused fieldset': { borderColor: '#22c55e' },
                  },
                  '& .MuiInputBase-input': { color: colors.textPrimary },
                  '& .MuiInputLabel-root': { color: colors.textSecondary },
                }}
              />
              )}
              {f.generateScript && (
                <Tooltip title="Generate">
                  <IconButton
                    size="small"
                    sx={{
                      bgcolor: 'rgba(34, 197, 94, 0.1)', border: `1px solid ${colors.border}`,
                      borderRadius: 0, width: 36, height: 36,
                      '&:hover': { bgcolor: 'rgba(34, 197, 94, 0.2)', borderColor: '#22c55e' },
                    }}
                    onClick={async () => {
                      try {
                        const resp = await FlexService.executeScript({ scriptName: f.generateScript, context: {} });
                        if (resp.success) {
                          const val = resp.context?.result?.[f.generateField || f.field] || '';
                          setFormValues(prev => ({ ...prev, [f.field]: val }));
                        }
                      } catch (err) { console.error('Generate failed:', err); }
                    }}
                  >
                    <AddIcon fontSize="small" sx={{ color: '#22c55e' }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          ))}
        </Box>
      )}

      {/* What the preview found, or why there is nothing to do */}
      {(loadingRows || notice) && (
        <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${colors.border}`, display: 'flex', alignItems: 'center', gap: 1 }}>
          {loadingRows && <CircularProgress size={16} sx={{ color: '#22c55e' }} />}
          <Typography variant="body2" sx={{ color: colors.textPrimary }}>
            {loadingRows ? 'Checking what needs doing...' : notice}
          </Typography>
        </Box>
      )}

      {/* Grid */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {/* the outer box scrolls, so the sticky header stays put while the rows scroll under it */}
        <TableContainer sx={{ overflow: 'visible' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" sx={{ bgcolor: colors.surfaceElevated, borderBottom: `1px solid ${colors.border}` }}>
                  <Checkbox
                    checked={allSelected}
                    indeterminate={selectedIds.size > 0 && !allSelected}
                    onChange={handleToggleAll}
                    sx={{ color: colors.textMuted, '&.Mui-checked': { color: '#22c55e' } }}
                  />
                </TableCell>
                {columns.map(col => (
                  <TableCell
                    key={col.field}
                    sx={{
                      bgcolor: colors.surfaceElevated,
                      color: colors.textSecondary,
                      fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase',
                      borderBottom: `1px solid ${colors.border}`,
                    }}
                  >
                    {col.label}
                  </TableCell>
                ))}
                <TableCell sx={{ bgcolor: colors.surfaceElevated, borderBottom: `1px solid ${colors.border}`, width: 40 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, idx) => (
                <TableRow
                  key={idx}
                  hover
                  selected={selectedIds.has(idx)}
                  sx={{
                    cursor: 'pointer',
                    '&.Mui-selected': { bgcolor: 'rgba(34, 197, 94, 0.08)' },
                    '&:hover': { bgcolor: colors.hoverBg },
                  }}
                  onClick={() => handleToggleRow(idx)}
                >
                  <TableCell padding="checkbox" sx={{ borderBottom: `1px solid ${colors.border}` }}>
                    <Checkbox
                      checked={selectedIds.has(idx)}
                      onChange={() => handleToggleRow(idx)}
                      sx={{ color: colors.textMuted, '&.Mui-checked': { color: '#22c55e' } }}
                    />
                  </TableCell>
                  {columns.map(col => (
                    <TableCell key={col.field} sx={{ borderBottom: `1px solid ${colors.border}`, color: colors.textPrimary, py: 1 }}>
                      {row[col.field] ?? ''}
                    </TableCell>
                  ))}
                  <TableCell sx={{ borderBottom: `1px solid ${colors.border}`, py: 1 }}>
                    <Tooltip title="Remove">
                      <IconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); handleRemoveRow(idx); }}
                        sx={{ color: colors.textMuted, '&:hover': { color: '#ef4444' } }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={columns.length + 2} sx={{ textAlign: 'center', py: 4, color: colors.textMuted }}>
                    No rows to plan
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Status bar */}
      {counters.length > 0 && (
        <Box sx={{
          display: 'flex', gap: 1.5, px: 2, py: 1,
          borderTop: `1px solid ${colors.border}`, bgcolor: colors.surface,
          flexWrap: 'wrap',
        }}>
          {counters.map((c, i) => (
            <Chip
              key={i}
              label={`${c.label}: ${c.value}`}
              size="small"
              sx={{
                bgcolor: 'rgba(34, 197, 94, 0.1)',
                color: colors.textPrimary,
                border: `1px solid ${colors.border}`,
                borderRadius: 0,
                fontSize: '0.75rem',
                fontWeight: 600,
              }}
            />
          ))}
          <Chip
            label={`Selected: ${selectedIds.size}`}
            size="small"
            sx={{
              bgcolor: 'rgba(34, 197, 94, 0.15)',
              color: '#22c55e',
              border: '1px solid #22c55e',
              borderRadius: 0,
              fontSize: '0.75rem',
              fontWeight: 600,
            }}
          />
        </Box>
      )}

      {/* Error */}
      {error && (
        <Box sx={{ px: 2, py: 1, bgcolor: 'rgba(239, 68, 68, 0.1)', borderTop: `1px solid ${colors.border}` }}>
          <Typography variant="body2" sx={{ color: '#ef4444' }}>{error}</Typography>
        </Box>
      )}

      {/* Footer */}
      <Box sx={{
        display: 'flex', justifyContent: 'flex-end', gap: 1,
        px: 2, py: 1.5, borderTop: `1px solid ${colors.border}`, bgcolor: colors.surface,
      }}>
        <Button
          onClick={onClose}
          sx={{ color: colors.textSecondary, borderRadius: 0 }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={saving || rows.length === 0 || selectedIds.size === 0}
          sx={{
            bgcolor: '#22c55e', color: '#09090b', borderRadius: 0,
            '&:hover': { bgcolor: '#16a34a' },
            '&.Mui-disabled': { bgcolor: colors.border, color: colors.textMuted },
          }}
        >
          {saving ? <CircularProgress size={20} sx={{ color: '#09090b' }} /> : `${confirmLabel} (${selectedIds.size})`}
        </Button>
      </Box>
    </Box>
  );
};

export default BulkConfirmOverlay;
